import { useState, useEffect } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../config/supabase'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

export interface AuthState {
  user: User | null
  session: Session | null
  loading: boolean
  error: string | null
}

export interface AuthActions {
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: AuthError | null }>
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<{ error: AuthError | null }>
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>
  updateProfile: (updates: {
    full_name?: string;
    avatar_url?: string;
    preferred_export_platform?: string;
    email_marketing?: boolean;
    email_updates?: boolean;
    email_security?: boolean;
  }) => Promise<{ error: AuthError | null }>
  uploadAvatar: (file: File) => Promise<{ data: string | null; error: Error | null }>
}

export function useAuth(): AuthState & AuthActions {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        if (error) {
          setError(error.message)
        } else {
          setSession(session)
          setUser(session?.user ?? null)
        }
      } catch (err) {
        setError('Failed to get session')
      } finally {
        setLoading(false)
      }
    }

    getInitialSession()

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
        
        if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
          setError(null)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      setLoading(true)
      setError(null)
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      })
      
      if (error) {
        setError(error.message)
        return { error }
      }
      
      return { error: null }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred during sign up'
      setError(errorMessage)
      return { error: { message: errorMessage } as AuthError }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true)
      setError(null)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      
      if (error) {
        setError(error.message)
        return { error }
      }
      
      return { error: null }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred during sign in'
      setError(errorMessage)
      return { error: { message: errorMessage } as AuthError }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    try {
      console.log('useAuth: signOut function called')
      setLoading(true)
      setError(null)
      
      console.log('useAuth: calling supabase.auth.signOut()')
      const { error } = await supabase.auth.signOut()
      console.log('useAuth: supabase.auth.signOut() completed with error:', error)
      
      if (error) {
        console.log('useAuth: signOut error occurred:', error.message)
        
        // If session is missing, treat as successful signout since user is effectively logged out
        if (error.message.includes('Auth session missing')) {
          console.log('useAuth: session missing, clearing all auth state')
          
          // Clear all local storage auth data
          try {
            localStorage.removeItem('supabase.auth.token')
            localStorage.removeItem('sb-' + supabaseUrl.split('//')[1].split('.')[0] + '-auth-token')
            sessionStorage.clear()
          } catch (e) {
            console.log('Failed to clear storage:', e)
          }
          
          // Clear local state
          setUser(null)
          setSession(null)
          setError(null)
          
          // Force reload to ensure clean state
          setTimeout(() => {
            console.log('useAuth: reloading page to ensure clean auth state')
            window.location.reload()
          }, 100)
          
          return { error: null }
        }
        
        setError(error.message)
        return { error }
      }
      
      console.log('useAuth: signOut successful')
      return { error: null }
    } catch (err) {
      console.error('useAuth: signOut exception:', err)
      const errorMessage = 'An unexpected error occurred during sign out'
      setError(errorMessage)
      return { error: { message: errorMessage } as AuthError }
    } finally {
      console.log('useAuth: signOut finally block, setting loading to false')
      setLoading(false)
    }
  }

  const resetPassword = async (email: string) => {
    try {
      setLoading(true)
      setError(null)
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}`,
      })
      
      if (error) {
        setError(error.message)
        return { error }
      }
      
      return { error: null }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred during password reset'
      setError(errorMessage)
      return { error: { message: errorMessage } as AuthError }
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (updates: {
    full_name?: string;
    avatar_url?: string;
    preferred_export_platform?: string;
    email_marketing?: boolean;
    email_updates?: boolean;
    email_security?: boolean;
  }) => {
    try {
      setLoading(true)
      setError(null)

      const { error } = await supabase.auth.updateUser({
        data: updates,
      })

      if (error) {
        setError(error.message)
        return { error }
      }

      return { error: null }
    } catch (err) {
      const errorMessage = 'An unexpected error occurred during profile update'
      setError(errorMessage)
      return { error: { message: errorMessage } as AuthError }
    } finally {
      setLoading(false)
    }
  }

  const uploadAvatar = async (file: File) => {
    try {
      if (!user) {
        return { data: null, error: new Error('User not authenticated') }
      }

      // Validate file type and size
      if (!file.type.startsWith('image/')) {
        return { data: null, error: new Error('Please select an image file') }
      }

      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        return { data: null, error: new Error('Image must be less than 5MB') }
      }

      // Create unique filename
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}-${Date.now()}.${fileExt}`

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        })

      if (error) {
        return { data: null, error: new Error(`Upload failed: ${error.message}`) }
      }

      // Get public URL
      const { data: publicData } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName)

      return { data: publicData.publicUrl, error: null }
    } catch (err) {
      return { data: null, error: new Error('Failed to upload avatar') }
    }
  }

  return {
    user,
    session,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updateProfile,
    uploadAvatar,
  }
}